import React, { ReactNode } from 'react'
import { FormattedMessage } from 'react-intl'
import { css, cx } from '@emotion/css'
import { Link } from '@contrast-security-inc/design-system-typography'

const toggleLinkCss = css`
  font-size: 12px;
  padding-top: 5px;
`

interface InnerLinkProps {
  readonly onClick: (e: React.SyntheticEvent<HTMLElement>) => void
  readonly className?: string
  readonly testId?: string
  readonly children: React.ReactNode
}

export function InnerLink({
  onClick,
  className,
  children,
  testId
}: InnerLinkProps) {
  return (
    <Link
      onClick={(e) => {
        e.stopPropagation()
        if (onClick) {
          onClick(e)
        }
      }}
      data-testid={testId}
      className={className ? cx(toggleLinkCss, className) : toggleLinkCss}
      children={children}
    />
  )
}

interface ToggleLinkProps {
  readonly onClick: (e: React.SyntheticEvent<HTMLElement>) => void
  readonly className?: string
  readonly total?: number
  count?: number
}

export function ExpandLink({ onClick, className, total = 0 }: ToggleLinkProps) {
  return (
    <InnerLink onClick={onClick} className={className}>
      {total > 0 ? (
        <FormattedMessage id="action_showAllWithTotal" values={{ total }} />
      ) : (
        <FormattedMessage id="action_showMore" />
      )}
    </InnerLink>
  )
}

export function CollapseLink({ onClick, className }: ToggleLinkProps) {
  return (
    <InnerLink onClick={onClick} className={className}>
      <FormattedMessage id="action_showLess" />
    </InnerLink>
  )
}

interface ToggleProps {
  readonly className?: string
  readonly isExpanded: boolean
  readonly onToggle: (isExpanded: boolean) => void
  readonly total?: number
}

export function CollapsibleListToggle({
  className,
  isExpanded,
  onToggle,
  total = 0
}: ToggleProps) {
  const LinkComponent = isExpanded ? CollapseLink : ExpandLink
  const props = {
    onClick: () => onToggle(!isExpanded),
    className,
    total
  }

  return <LinkComponent {...props} />
}

interface CollapsibleListManagerChildrenProps<T> {
  readonly visibleItems: T[]
  readonly isExpanded: boolean
  readonly onToggle: (isExpanded: boolean) => void
  readonly showToggle: boolean
}

interface CollapsibleListItemsProps<T> {
  readonly max: number
  readonly children: (
    props: CollapsibleListManagerChildrenProps<T>
  ) => ReactNode
  readonly items: T[]
}
interface CollapsibleListItemsState {
  readonly isExpanded: boolean
}
export class CollapsibleListManager<T> extends React.Component<
  CollapsibleListItemsProps<T>,
  CollapsibleListItemsState
> {
  public state = {
    isExpanded: false
  }
  public render() {
    const { max, items, children } = this.props
    const { isExpanded } = this.state
    return children({
      visibleItems: isExpanded ? items : items.slice(0, max),
      isExpanded,
      onToggle: this.onToggle,

      showToggle: items.length > max
    })
  }
  private onToggle = (isExpanded) => {
    this.setState({
      isExpanded: !!isExpanded
    })
  }
}
